var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { Box, Typography, Paper, Tabs, Tab, Alert, Snackbar, } from '@mui/material';
import { userService } from '@services/userService';
import { useAuth } from '@contexts/AuthContext';
import UserManagement from './UserManagement';
import RoleManagement from './RoleManagement';
import AdminMachines from './AdminMachines';
import AdminServiceHistory from './AdminServiceHistory';
import AdminJobs from './AdminJobs';
const TabPanel = ({ children, value, index }) => {
    return (_jsx("div", { role: "tabpanel", hidden: value !== index, children: value === index && _jsx(Box, { sx: { p: 3 }, children: children }) }));
};
const Admin = () => {
    var _a, _b;
    const { user } = useAuth();
    const [tabValue, setTabValue] = useState(0);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success',
    });
    useEffect(() => {
        var _a, _b;
        if (((_a = user === null || user === void 0 ? void 0 : user.roles) === null || _a === void 0 ? void 0 : _a.includes('Admin')) || ((_b = user === null || user === void 0 ? void 0 : user.roles) === null || _b === void 0 ? void 0 : _b.includes('Owner'))) {
            loadUsers();
        }
    }, [user]);
    const loadUsers = () => __awaiter(void 0, void 0, void 0, function* () {
        try {
            setLoading(true);
            console.log('Loading users...');
            const data = yield userService.getUsers();
            console.log('Users loaded:', data);
            setUsers(data);
        }
        catch (error) {
            console.error('Error loading users:', error);
            showSnackbar((error === null || error === void 0 ? void 0 : error.message) || 'Error loading users', 'error');
        }
        finally {
            setLoading(false);
        }
    });
    const handleCreateUser = (userData) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            yield userService.createUser(userData);
            showSnackbar('User created successfully', 'success');
            loadUsers();
        }
        catch (error) {
            console.error('Error creating user:', error);
            showSnackbar(error.message || 'Error creating user', 'error');
            throw error;
        }
    });
    const handleUpdateUser = (id, userData) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            yield userService.updateUser(id, userData);
            showSnackbar('User updated successfully', 'success');
            loadUsers();
        }
        catch (error) {
            console.error('Error updating user:', error);
            showSnackbar(error.message || 'Error updating user', 'error');
            throw error;
        }
    });
    const handleDeleteUser = (id) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            yield userService.deleteUser(id);
            showSnackbar('User deleted successfully', 'success');
            loadUsers();
        }
        catch (error) {
            console.error('Error deleting user:', error);
            showSnackbar(error.message || 'Error deleting user', 'error');
            throw error;
        }
    });
    const handleUpdateUserRoles = (id, roles) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            yield userService.updateUserRoles(id, roles);
            showSnackbar('User roles updated successfully', 'success');
            loadUsers();
        }
        catch (error) {
            console.error('Error updating user roles:', error);
            showSnackbar(error.message || 'Error updating user roles', 'error');
            throw error;
        }
    });
    const showSnackbar = (message, severity) => {
        setSnackbar({ open: true, message, severity });
    };
    const handleCloseSnackbar = () => {
        setSnackbar(Object.assign(Object.assign({}, snackbar), { open: false }));
    };
    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };
    if (!((_a = user === null || user === void 0 ? void 0 : user.roles) === null || _a === void 0 ? void 0 : _a.includes('Admin')) && !((_b = user === null || user === void 0 ? void 0 : user.roles) === null || _b === void 0 ? void 0 : _b.includes('Owner'))) {
        return (_jsx(Box, { children: _jsx(Alert, { severity: "error", children: "You do not have permission to access this page." }) }));
    }
    return (_jsxs(Box, { children: [_jsx(Typography, { variant: "h4", sx: { mb: 3 }, children: "Admin Dashboard" }), _jsxs(Paper, { sx: { width: '100%' }, children: [_jsxs(Tabs, { value: tabValue, onChange: handleTabChange, children: [_jsx(Tab, { label: "Overview" }), _jsx(Tab, { label: "Users" }), _jsx(Tab, { label: "Roles" }), _jsx(Tab, { label: "Machines" }), _jsx(Tab, { label: "Service History" }), _jsx(Tab, { label: "Jobs" })] }), _jsxs(TabPanel, { value: tabValue, index: 0, children: [_jsx(Typography, { variant: "h6", gutterBottom: true, children: "System Overview" }), _jsx(Alert, { severity: "info", sx: { mt: 2 }, children: "Welcome to the Admin Dashboard. Use the tabs above to manage users and view role definitions." }), _jsxs(Box, { sx: { mt: 3 }, children: [_jsxs(Typography, { variant: "body1", paragraph: true, children: [_jsx("strong", { children: "Total Users:" }), " ", users.length] }), _jsxs(Typography, { variant: "body1", paragraph: true, children: [_jsx("strong", { children: "Admin Users:" }), " ", users.filter(u => { var _a; return (_a = u.roles) === null || _a === void 0 ? void 0 : _a.includes('Admin'); }).length] }), _jsxs(Typography, { variant: "body1", paragraph: true, children: [_jsx("strong", { children: "Owner Users:" }), " ", users.filter(u => { var _a; return (_a = u.roles) === null || _a === void 0 ? void 0 : _a.includes('Owner'); }).length] }), _jsxs(Typography, { variant: "body1", paragraph: true, children: [_jsx("strong", { children: "Regular Users:" }), " ", users.filter(u => { var _a; return ((_a = u.roles) === null || _a === void 0 ? void 0 : _a.length) === 1 && u.roles[0] === 'User'; }).length] })] })] }), _jsx(TabPanel, { value: tabValue, index: 1, children: _jsx(UserManagement, { users: users, loading: loading, onRefresh: loadUsers, onCreateUser: handleCreateUser, onUpdateUser: handleUpdateUser, onDeleteUser: handleDeleteUser, onUpdateUserRoles: handleUpdateUserRoles }) }), _jsx(TabPanel, { value: tabValue, index: 2, children: _jsx(RoleManagement, {}) }), _jsx(TabPanel, { value: tabValue, index: 3, children: _jsx(AdminMachines, {}) }), _jsx(TabPanel, { value: tabValue, index: 4, children: _jsx(AdminServiceHistory, {}) }), _jsx(TabPanel, { value: tabValue, index: 5, children: _jsx(AdminJobs, {}) })] }), _jsx(Snackbar, { open: snackbar.open, autoHideDuration: 6000, onClose: handleCloseSnackbar, anchorOrigin: { vertical: 'bottom', horizontal: 'center' }, children: _jsx(Alert, { onClose: handleCloseSnackbar, severity: snackbar.severity, sx: { width: '100%' }, children: snackbar.message }) })] }));
};
export default Admin;
